class GoogleDriveService {
  constructor() {
    this.initialized = false;
    this.apiKey = 'AIzaSyBO7DR_jSm4wdQPY6Twx5fUFovM4ty-nC4'; // Your existing API key from firebase config
  }

  /**
   * Initialize Google Drive API for browser use
   * Using a simplified approach without discovery documents
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Load Google API client library if not already loaded
      if (!window.gapi) {
        await this.loadGoogleAPI();
      }

      // Initialize the API client with a simpler approach
      await new Promise((resolve, reject) => {
        window.gapi.load('client', async () => {
          try {
            await window.gapi.client.init({
              apiKey: this.apiKey
            });
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      });

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Google Drive API:', error);
      throw new Error('Failed to initialize Google Drive API. Please check your API key.');
    }
  }

  /**
   * Load Google API client library
   */
  async loadGoogleAPI() {
    return new Promise((resolve, reject) => {
      if (window.gapi) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Extract folder ID from Google Drive URL
   */
  extractFolderIdFromUrl(url) {
    if (!url || typeof url !== 'string') {
      throw new Error('Invalid Google Drive URL');
    }

    // Pattern 1: /folders/FOLDER_ID
    let match = url.match(/\/folders\/([a-zA-Z0-9-_]+)/);
    if (match) {
      return match[1];
    }

    // Pattern 2: ?id=FOLDER_ID
    match = url.match(/[?&]id=([a-zA-Z0-9-_]+)/);
    if (match) {
      return match[1];
    }

    // If no pattern matches, assume the URL is just the folder ID
    const cleanId = url.trim().replace(/[?&].*$/, '');
    if (/^[a-zA-Z0-9-_]+$/.test(cleanId)) {
      return cleanId;
    }

    throw new Error('Could not extract folder ID from URL. Please check the URL format.');
  }

  /**
   * Get list of image files from publicly shared Google Drive folder
   * Using direct API calls instead of discovery documents
   */
  async getImagesFromFolder(folderId) {
    await this.initialize();

    try {
      // Make direct API call to Google Drive API
      // Request additional fields including permissions to verify file is accessible
      const url = `https://www.googleapis.com/drive/v3/files?q='${folderId}' in parents and (mimeType contains 'image/' or mimeType='video/mp4') and trashed=false&fields=files(id,name,mimeType,size,webContentLink,webViewLink,permissions)&pageSize=1000&key=${this.apiKey}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Folder not found. Please check the folder ID and ensure it is publicly accessible.');
        } else if (response.status === 403) {
          throw new Error('Access denied. Please ensure the folder is publicly shared with "Anyone with the link can view".');
        } else {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      const files = data.files || [];
      
      // Filter for supported file types
      const supportedTypes = [
        'image/jpeg', 
        'image/jpg', 
        'image/png', 
        'image/webp',
        'video/mp4'
      ];

      const supportedFiles = files.filter(file => 
        supportedTypes.includes(file.mimeType) || 
        supportedTypes.some(type => file.mimeType.startsWith(type.split('/')[0]))
      );

      return supportedFiles.map(file => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        size: parseInt(file.size) || 0,
        isVideo: file.mimeType.startsWith('video/'),
        downloadUrl: `https://drive.google.com/uc?export=download&id=${file.id}`
      }));
    } catch (error) {
      console.error('Error fetching files from Google Drive:', error);
      throw error;
    }
  }

  /**
   * Download file from Google Drive using the API
   */
  async downloadFile(fileId, fileName) {
    try {
      // Use the Drive API to get file content
      const url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&key=${this.apiKey}`;
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors'
      });
      
      if (!response.ok) {
        // Try alternative download URL for files that need confirmation
        if (response.status === 403 || response.status === 401) {
          throw new Error('File access denied. Ensure the file is publicly accessible.');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Verify the blob is actually an image/video
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Check if we got an HTML error page instead of the file
      if (blob.type === 'text/html' || blob.type === 'text/plain') {
        // This might be an error page - check content
        const text = await blob.text();
        if (text.includes('Google Drive') || text.includes('error')) {
          throw new Error('Received error page instead of file. File may require additional permissions.');
        }
        // If it's actual HTML/text content, convert back to blob
        return {
          blob: new Blob([text], { type: blob.type }),
          fileName: fileName
        };
      }
      
      return {
        blob: blob,
        fileName: fileName
      };
    } catch (error) {
      console.error(`Error downloading file ${fileName}:`, error);
      
      // Try alternative download method using webContentLink
      try {
        console.log(`Attempting alternative download method for ${fileName}...`);
        const directUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
        
        const altResponse = await fetch(directUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (altResponse.ok) {
          const blob = await altResponse.blob();
          if (blob.size > 0 && blob.type !== 'text/html') {
            return {
              blob: blob,
              fileName: fileName
            };
          }
        }
      } catch (altError) {
        console.error(`Alternative download also failed for ${fileName}:`, altError);
      }
      
      throw new Error(`Failed to download ${fileName}. The file may not be publicly accessible.`);
    }
  }

  /**
   * Convert blob to File object for upload
   */
  blobToFile(blob, fileName) {
    return new File([blob], fileName, { 
      type: blob.type,
      lastModified: Date.now()
    });
  }

  /**
   * Import all images from Google Drive folder
   * Returns array of File objects ready for upload
   */
  async importImagesFromFolder(folderUrl, progressCallback = null) {
    try {
      // Extract folder ID from URL
      const folderId = this.extractFolderIdFromUrl(folderUrl);
      
      // Get list of image files
      const imageFiles = await this.getImagesFromFolder(folderId);
      
      if (imageFiles.length === 0) {
        throw new Error('No supported image or video files found in the folder.');
      }

      console.log(`Found ${imageFiles.length} files to download:`, imageFiles.map(f => f.name));

      // Download all files with progress tracking
      const files = [];
      const errors = [];
      
      for (let i = 0; i < imageFiles.length; i++) {
        try {
          console.log(`Downloading ${i + 1}/${imageFiles.length}: ${imageFiles[i].name}`);
          
          // Call progress callback if provided
          if (progressCallback) {
            progressCallback({
              current: i + 1,
              total: imageFiles.length,
              fileName: imageFiles[i].name,
              percentage: Math.round(((i + 1) / imageFiles.length) * 100)
            });
          }
          
          const result = await this.downloadFile(imageFiles[i].id, imageFiles[i].name);
          const file = this.blobToFile(result.blob, result.fileName);
          files.push(file);
          
          console.log(`✓ Successfully downloaded: ${imageFiles[i].name} (${(result.blob.size / 1024).toFixed(2)} KB)`);
        } catch (error) {
          const errorMsg = `Failed to download ${imageFiles[i].name}: ${error.message}`;
          console.error(`✗ ${errorMsg}`);
          errors.push(errorMsg);
        }
      }

      console.log(`Download complete: ${files.length} succeeded, ${errors.length} failed`);

      return {
        files: files,
        errors: errors,
        totalFound: imageFiles.length,
        successCount: files.length
      };
    } catch (error) {
      console.error('Error importing images from Google Drive:', error);
      throw error;
    }
  }
}

export default GoogleDriveService;